import React from 'react';
import { Box, Typography, Button, SvgIconProps } from '@mui/material';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SearchOffIcon from '@mui/icons-material/SearchOff';

export interface EmptyStateProps {
  /** Visual variant — drives the default icon. */
  variant?: 'empty' | 'no-results' | 'error';
  /** Custom icon (overrides the variant default). */
  icon?: React.ReactElement<SvgIconProps>;
  title?: React.ReactNode;
  description?: React.ReactNode;
  /** Optional primary action (e.g. "افزودن", "تلاش مجدد"). */
  actionLabel?: React.ReactNode;
  onAction?: () => void;
  /** Compact mode for use inside cards/sections. */
  dense?: boolean;
}

const DEFAULTS = {
  empty: { icon: <InboxOutlinedIcon />, title: 'موردی یافت نشد' },
  'no-results': { icon: <SearchOffIcon />, title: 'نتیجه‌ای مطابق فیلترها پیدا نشد' },
  error: { icon: <ErrorOutlineIcon />, title: 'خطا در بارگذاری اطلاعات' },
} as const;

/**
 * Consistent empty / no-results / error state. Theme-token driven, centered,
 * with an optional call-to-action. Use for list/table empties and failed loads.
 */
const EmptyState: React.FC<EmptyStateProps> = ({
  variant = 'empty',
  icon,
  title,
  description,
  actionLabel,
  onAction,
  dense = false,
}) => {
  const def = DEFAULTS[variant];
  const resolvedIcon = icon ?? def.icon;
  const color = variant === 'error' ? 'error.main' : 'text.disabled';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        gap: 1,
        px: 2,
        py: dense ? 4 : 8,
      }}
    >
      {React.cloneElement(resolvedIcon as React.ReactElement<SvgIconProps>, {
        sx: { fontSize: dense ? 44 : 64, color, mb: 0.5 },
      })}
      <Typography variant={dense ? 'subtitle1' : 'h6'} sx={{ fontWeight: 600 }}>
        {title ?? def.title}
      </Typography>
      {description && (
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420 }}>
          {description}
        </Typography>
      )}
      {actionLabel && onAction && (
        <Button
          variant={variant === 'error' ? 'outlined' : 'contained'}
          color={variant === 'error' ? 'error' : 'primary'}
          onClick={onAction}
          sx={{ mt: 1.5 }}
        >
          {actionLabel}
        </Button>
      )}
    </Box>
  );
};

export default EmptyState;
